import React, { useCallback, useEffect, useState } from "react";
import { Scale, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { floorJudge, floorJudgeDecide } from "../lib/api";

const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const tone = (v) => (v === null || v === undefined ? "text-slate-500" : v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : "text-slate-300");

const AgentTable = ({ rows }) => (
  <table className="w-full text-[11px]" data-testid="judge-agent-table">
    <thead className="text-slate-500"><tr><th className="text-left">Agent</th><th>Pass</th><th>Veto</th><th>Pass win</th><th>Veto right</th><th>Missed avg</th></tr></thead>
    <tbody className="text-slate-300">{rows.map((a) => (
      <tr key={a.agent} className="border-t border-white/5" data-testid={`judge-agent-${a.agent.split(" ")[1] || a.agent}`}>
        <td className="py-1 text-slate-200">{a.agent}</td><td className="text-center">{a.passed}</td><td className="text-center">{a.vetoed}</td>
        <td className="text-center">{pct(a.pass_win_rate)}</td><td className="text-center">{pct(a.veto_accuracy)}</td>
        <td className={`text-center ${tone(a.missed_avg_return)}`}>{pct(a.missed_avg_return)}</td>
      </tr>))}</tbody>
  </table>
);

const ShadowBlock = ({ s }) => (
  <div className="space-y-2" data-testid="judge-shadow">
    <div className="grid grid-cols-2 gap-2 text-xs">
      <div className="rounded-xl bg-white/5 p-2"><p className="text-slate-500">Floor (Genie passes)</p>
        <p className={`font-bold ${tone(s.floor_avg_return)}`} data-testid="judge-floor-avg">{pct(s.floor_avg_return)} avg · win {pct(s.floor_win_rate)}</p>
        <p className="text-slate-500">{s.floor_scored}/{s.floor_passes} settled</p></div>
      <div className="rounded-xl bg-white/5 p-2"><p className="text-slate-500">TradingAgents shadow</p>
        <p className={`font-bold ${tone(s.shadow_avg_return)}`} data-testid="judge-shadow-avg">{pct(s.shadow_avg_return)} avg · win {pct(s.shadow_win_rate)}</p>
        <p className="text-slate-500">{s.shadow_scored}/{s.shadow_passes} settled · {s.scored_sessions}/5 sessions</p></div>
    </div>
    <p className="text-[11px] text-slate-400" data-testid="judge-shadow-recent">Latest shadow calls: {s.recent.length ? s.recent.slice(0, 6).map((r) => `${r.ticker} ${r.rating || "—"}${r.confidence ? " " + r.confidence : ""}${r.passed ? " ✓" : ""}`).join(" · ") : "none yet — runs after each stocks/crypto round"}</p>
    <p className="text-[11px] text-amber-200/80 font-bold">Shadow would trade, floor vetoed ({s.flags.length})</p>
    <div className="max-h-40 overflow-y-auto space-y-1" data-testid="judge-flags">
      {s.flags.length === 0 && <p className="text-[11px] text-slate-500">None yet.</p>}
      {s.flags.map((f, i) => (
        <div key={i} className="text-[11px] rounded-lg bg-amber-500/5 border border-amber-400/20 px-2 py-1" data-testid={`judge-flag-${i}`}>
          <span className="font-bold text-amber-200">{f.ticker}</span> {f.direction} · {f.rating} {f.confidence} · vetoed by <b>{f.floor_veto_name || "—"}</b>
          <span className={`ml-1 ${tone(f.shadow_return)}`}>{f.shadow_return !== null && f.shadow_return !== undefined ? `${f.shadow_return}%` : "pending"}</span>
          {f.floor_veto_reason && <p className="text-slate-500 truncate">{f.floor_veto_reason}</p>}
        </div>))}
    </div>
  </div>
);

const Proposal = ({ p, onDecide }) => (
  <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/5 p-3 text-xs space-y-2" data-testid="judge-proposal">
    <p className="font-bold text-emerald-200">Proposal: long gate {p.long_gate} / short gate {p.short_gate}</p>
    <p className="text-slate-400">Shadow {pct(p.evidence.shadow_avg_return)} vs floor {pct(p.evidence.floor_avg_return)} over {p.evidence.scored_sessions} sessions. Or reply APPROVE in Nexus chat.</p>
    <div className="flex gap-2">
      <button onClick={() => onDecide("approve")} className="px-3 py-1 rounded-lg bg-emerald-400 text-black font-bold" data-testid="judge-approve">Approve</button>
      <button onClick={() => onDecide("reject")} className="px-3 py-1 rounded-lg border border-rose-400/40 text-rose-300 font-bold" data-testid="judge-reject">Reject</button>
    </div>
  </div>
);

export const FloorJudgeCard = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => floorJudge().then(setD).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const decide = async (k) => { try { toast.success((await floorJudgeDecide(k)).report); load(); } catch (e) { toast.error("Could not update the proposal"); } };
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3" data-testid="floor-judge-card">
      <div className="flex items-center justify-between">
        <p className="text-[#00F0FF] font-bold flex items-center gap-2"><Scale className="w-4 h-4" /> Score the floor</p>
        {d && <span className="text-[11px] text-slate-500" data-testid="judge-summary">{d.score.decisions} calls · {d.score.scored} settled · gates {d.gates.long}/{d.gates.short}</span>}
      </div>
      {!d ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : (<>
        {d.proposal && <Proposal p={d.proposal} onDecide={decide} />}
        <AgentTable rows={d.score.by_agent} />
        <ShadowBlock s={d.shadow} />
      </>)}
    </div>
  );
};
